import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { Input } from 'components/Input'
import { ManagementFormLayout } from 'components/ManagementFormLayout'
import { OrganizationFeatureField } from 'components/OrganizationFeatureField'
import { OrganizationPlanField } from 'components/OrganizationPlanField'
import { Snackbar } from 'components/Snackbar'
import { routes } from 'constants/routes'
import { commonStrings, organizationManagementStrings } from 'constants/strings'
import { useAdminOrganizations, useUpdateOrganization, useLineChannelAccounts, useSendMigrationMessage } from 'hooks'
import { EOrganizationPlan } from 'types/enum'
import type { TOrganizationFeatures } from 'types/organization'
import { DISABLED_ORGANIZATION_FEATURES } from 'utils/organizationFeature'
import { DeveloperSyncModal } from 'pages/Line/DeveloperSyncModal'
import { MigrationModal } from 'pages/Line/MigrationModal'
import { OamSettingsModal } from 'pages/Line/OamSettingsModal'

export default function OrganizationManagementEdit() {
  const navigate = useNavigate()
  const { organizationId } = useParams()
  const strings = organizationManagementStrings.edit
  const [name, setName] = useState('')
  const [plan, setPlan] = useState<EOrganizationPlan>(EOrganizationPlan.INTERNAL)
  const [features, setFeatures] = useState<TOrganizationFeatures>({
    ...DISABLED_ORGANIZATION_FEATURES,
  })
  const [validationError, setValidationError] = useState<string | undefined>()
  const [isDeveloperSyncOpen, setIsDeveloperSyncOpen] = useState(false)
  const [isOamSettingsOpen, setIsOamSettingsOpen] = useState(false)
  const [isMigrationOpen, setIsMigrationOpen] = useState(false)
  const [isMsgSnackbarOpen, setIsMsgSnackbarOpen] = useState(false)
  const [msgSnackbarMessage, setMsgSnackbarMessage] = useState('')
  const [msgSnackbarVariant, setMsgSnackbarVariant] = useState<'success' | 'error'>('success')
  const migrationMessage = useSendMigrationMessage()

  const organizationIdNumber = Number(organizationId)
  const hasValidId =
    Number.isFinite(organizationIdNumber) &&
    Number.isInteger(organizationIdNumber) &&
    organizationIdNumber > 0

  const { accounts, loading: accountsLoading } = useLineChannelAccounts(hasValidId ? organizationIdNumber : undefined)
  const { send: sendMigrationMsg, isRunning: isSendingMessage } = migrationMessage

  const handleSendMigrationMessage = async () => {
    if (isSendingMessage || accounts.length === 0) return
    const results = await sendMigrationMsg(accounts.map((a) => a.id), organizationIdNumber)
    const hasError = results.some((r) => r.status === 'error')
    setMsgSnackbarMessage(hasError ? '移行メッセージの送信に一部失敗しました。' : '移行メッセージを送信しました。')
    setMsgSnackbarVariant(hasError ? 'error' : 'success')
    setIsMsgSnackbarOpen(true)
  }

  const { organizations, loading, error: loadError } = useAdminOrganizations()
  const organization = useMemo(() => {
    if (!hasValidId) return undefined
    return organizations.find((item) => item.id === organizationIdNumber)
  }, [hasValidId, organizationIdNumber, organizations])

  const { update, isSubmitting, error: updateError } = useUpdateOrganization({
    onSuccess: () =>
      navigate(routes.ORGANIZATION_MANAGEMENT, {
        replace: true,
        state: { organizationUpdated: true },
      }),
  })

  useEffect(() => {
    if (!organization) return
    setName(organization.name)
    setPlan(organization.plan)
    setFeatures({ ...DISABLED_ORGANIZATION_FEATURES, ...organization.features })
  }, [organization])

  const notFound =
    !loading && !loadError && (!hasValidId || !organization)
  const isFormDisabled = loading || isSubmitting || !hasValidId || !organization
  const error =
    validationError ||
    updateError ||
    loadError ||
    (notFound ? organizationManagementStrings.states.notFound : undefined)

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isFormDisabled) return

    setValidationError(undefined)
    const trimmedName = name.trim()
    if (!trimmedName) {
      setValidationError(strings.errors.required)
      return
    }

    update(organizationIdNumber, { name: trimmedName, plan, features })
  }

  return (
    <ManagementFormLayout
      kicker={strings.kicker}
      title={strings.title}
      description={strings.description}
      backTo={routes.ORGANIZATION_MANAGEMENT}
    >
      <form
        className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm"
        onSubmit={handleSubmit}
      >
        <div className="grid gap-1">
          <h2 className="text-sm font-semibold text-ink">
            {strings.form.title}
          </h2>
          <p className="text-xs text-muted">{strings.form.description}</p>
        </div>
        <div className="grid gap-4">
          <label className="grid gap-2 text-xs font-semibold text-ink">
            {strings.form.fields.name.label}
            <Input
              name="name"
              placeholder={strings.form.fields.name.placeholder}
              autoComplete="organization"
              required
              disabled={isFormDisabled}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <OrganizationPlanField
            label={strings.form.fields.plan.label}
            value={plan}
            onChange={setPlan}
            disabled={isFormDisabled}
          />
          <OrganizationFeatureField
            label={strings.form.fields.features.label}
            value={features}
            onChange={setFeatures}
            disabled={isFormDisabled}
          />
        </div>
        {error && <Alert>{error}</Alert>}
        <div className="flex justify-end">
          <Button size="md" type="submit" disabled={isFormDisabled}>
            {isSubmitting
              ? `${strings.actions.submit}${commonStrings.loadingEllipsis}`
              : strings.actions.submit}
          </Button>
        </div>
      </form>

      <div className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm">
        <div className="grid gap-1">
          <h2 className="text-sm font-semibold text-ink">LINE連携</h2>
          <p className="text-xs text-muted">①初期設定 → ②チャンネル連携 の順で実行してください。</p>
        </div>
        <div className="flex gap-3">
          <Button
            type="button"
            size="sm"
            disabled={!hasValidId}
            onClick={() => setIsOamSettingsOpen(true)}
          >
            ①初期設定
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!hasValidId}
            onClick={() => setIsDeveloperSyncOpen(true)}
          >
            ②チャンネル連携
          </Button>
        </div>
      </div>

      <div className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm">
        <div className="grid gap-1">
          <h2 className="text-sm font-semibold text-ink">移行</h2>
          <p className="text-xs text-muted">LINEアカウントと友だち情報の移行を行います。</p>
        </div>
        <div className="flex gap-3">
          <Button
            type="button"
            size="sm"
            disabled={!hasValidId}
            onClick={() => setIsMigrationOpen(true)}
          >
            移行
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!hasValidId || isSendingMessage || accountsLoading || accounts.length === 0}
            onClick={handleSendMigrationMessage}
          >
            {isSendingMessage ? '送信中...' : accountsLoading ? '読み込み中...' : `移行メッセージ送信${accounts.length > 0 ? `（${accounts.length}件）` : ''}`}
          </Button>
        </div>
        {!accountsLoading && accounts.length === 0 && hasValidId && (
          <p className="text-xs text-muted">LINEアカウントが登録されていません。先にLINE連携を行ってください。</p>
        )}
      </div>

      <DeveloperSyncModal
        open={isDeveloperSyncOpen}
        onClose={() => setIsDeveloperSyncOpen(false)}
        organizationId={organizationIdNumber}
      />
      <OamSettingsModal
        open={isOamSettingsOpen}
        onClose={() => setIsOamSettingsOpen(false)}
      />
      <MigrationModal
        open={isMigrationOpen}
        onClose={() => setIsMigrationOpen(false)}
        messageSend={migrationMessage}
        organizationId={organizationIdNumber}
      />
      <Snackbar
        open={isMsgSnackbarOpen}
        message={msgSnackbarMessage}
        variant={msgSnackbarVariant}
        position="top-center"
        autoHideDuration={3000}
        onClose={() => setIsMsgSnackbarOpen(false)}
      />
    </ManagementFormLayout>
  )
}
